import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, formatCZK, formatDateTime, formatQty } from "@/components/Shared";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, Clock } from "lucide-react";
import SearchBar from "@/components/SearchBar";
import { formatQtyWithPackage } from "@/lib/units";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const STATUS_LABEL = {
  pending: "Čeká na přijetí",
  received: "Přijato",
  received_with_issue: "Přijato s výhradou",
};

const STATUS_COLOR = {
  pending: { bg: "#C29329", fg: "#FFFFFF", soft: "#C293291A" },
  received: { bg: "#376A4D", fg: "#FFFFFF", soft: "#376A4D1A" },
  received_with_issue: { bg: "#9E4747", fg: "#FFFFFF", soft: "#9E47471A" },
};

export default function BranchReceipts() {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [products, setProducts] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [filterBranch, setFilterBranch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState(null); // { dispatch, mode }
  const [recvQty, setRecvQty] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const [b, p, d, t] = await Promise.all([
      api.get("/branches"),
      api.get("/products", { params: { include_deleted: true } }),
      api.get("/dispatches"),
      api.get("/transfers"),
    ]);
    setBranches(b.data);
    setProducts(p.data);
    // map transfers to look like dispatches for the table
    const transferAsDispatch = t.data.map((tr) => ({
      ...tr,
      _kind: "transfer",
      from_warehouse_id: tr.from_branch_id,
      branch_id: tr.to_branch_id,
    }));
    const all = [
      ...d.data.map((x) => ({ ...x, _kind: "dispatch" })),
      ...transferAsDispatch,
    ].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    setDispatches(all);
    if (user.role !== "admin" && user.branch_id) setFilterBranch(user.branch_id);
  };

  useEffect(() => {
    load();
  }, []);

  const prodName = (id) => products.find((p) => p.id === id)?.name || "—";
  const brName = (id) => branches.find((b) => b.id === id)?.name || "—";
  const canConfirm = (d) => user.role === "admin" || user.branch_id === d.branch_id;

  const visible = useMemo(() => {
    let list = dispatches;
    if (filterBranch) list = list.filter((d) => d.branch_id === filterBranch);
    if (statusFilter !== "all") list = list.filter((d) => (d.status || "received") === statusFilter);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((d) => {
        const fields = [prodName(d.product_id), brName(d.branch_id), d.discrepancy_note].filter(Boolean);
        return fields.some((f) => String(f).toLowerCase().includes(q));
      });
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatches, filterBranch, statusFilter, search, products, branches]);

  const totals = useMemo(() => {
    return visible.reduce(
      (acc, d) => {
        acc.qty += d.quantity;
        acc.received += d.received_quantity || 0;
        acc.cost += d.total_cost || 0;
        return acc;
      },
      { qty: 0, received: 0, cost: 0 }
    );
  }, [visible]);

  const openOk = (d) => {
    setDialog({ dispatch: d, mode: "ok" });
    setRecvQty(String(d.quantity));
    setNote("");
  };
  const openIssue = (d) => {
    setDialog({ dispatch: d, mode: "issue" });
    setRecvQty(String(d.quantity));
    setNote("");
  };

  const submit = async () => {
    if (!dialog) return;
    setSubmitting(true);
    try {
      const body =
        dialog.mode === "ok"
          ? { mode: "ok" }
          : {
              mode: "issue",
              received_quantity: parseFloat(recvQty),
              discrepancy_note: note.trim(),
            };
      const path = dialog.dispatch._kind === "transfer"
        ? `/transfers/${dialog.dispatch.id}/receive`
        : `/dispatches/${dialog.dispatch.id}/receive`;
      await api.post(path, body);
      toast.success(dialog.mode === "ok" ? "Příjem potvrzen" : "Přijato s výhradou — rozpor zaznamenán");
      setDialog(null);
      setRecvQty("");
      setNote("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chyba");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="branch-receipts-page">
      <PageHeader
        eyebrow="Pobočka"
        title="Příjem na pobočce"
        description="Záznamy expedic na vaši pobočku. Před vyskladněním z výrobního skladu je nutné každou zásilku potvrdit tlačítkem Přijato, nebo Přijato s výhradou (kdy se zapíše rozpor)."
      />

      <div className="card-cafe p-4 mb-6 space-y-3">
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Hledat podle výrobku, pobočky nebo rozporu…"
          testid="branch-receipts-search"
        />
        <div className="flex flex-wrap items-center gap-3">
          {user.role === "admin" && (
            <>
              <span className="text-sm text-[#57534E]">Pobočka:</span>
              <select
                data-testid="branch-filter"
                className="input-cafe py-1.5"
                value={filterBranch}
                onChange={(e) => setFilterBranch(e.target.value)}
              >
                <option value="">— všechny —</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </>
          )}
          <span className="text-sm text-[#57534E] ml-2">Stav:</span>
          <select
            data-testid="status-filter"
            className="input-cafe py-1.5"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Vše</option>
            <option value="pending">Čeká na přijetí</option>
            <option value="received">Přijato</option>
            <option value="received_with_issue">Přijato s výhradou</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-5 mb-6">
        <Stat label="Záznamů" value={visible.length} />
        <Stat label="Odesláno ks" value={formatQty(totals.qty)} />
        <Stat label="Přijato ks" value={formatQty(totals.received)} accent="#376A4D" />
        <Stat label="Hodnota výroby" value={formatCZK(totals.cost)} accent="#3A2D27" />
      </div>

      {/* Mobile cards (< md) */}
      <div className="md:hidden space-y-3 mb-2">
        {visible.length === 0 ? (
          <div className="card-cafe p-8 text-center text-[#57534E] text-sm">
            Žádné záznamy příjmu.
          </div>
        ) : (
          visible.map((d) => {
            const status = d.status || "received";
            const colors = STATUS_COLOR[status];
            const isPending = status === "pending";
            const product = products.find((p) => p.id === d.product_id);
            return (
              <div
                key={d.id}
                data-testid={`branch-receipt-card-${d.id}`}
                className="card-cafe p-4"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-base truncate">{prodName(d.product_id)}</div>
                    <div className="text-xs text-[#57534E] mt-0.5">
                      {brName(d.branch_id)} · {formatDateTime(d.created_at)}
                    </div>
                  </div>
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium shrink-0"
                    style={{ background: colors.soft, color: colors.bg }}
                  >
                    {status === "pending" && <Clock className="w-3 h-3" />}
                    {status === "received" && <CheckCircle2 className="w-3 h-3" />}
                    {status === "received_with_issue" && <AlertTriangle className="w-3 h-3" />}
                    {STATUS_LABEL[status]}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm mt-3">
                  <div>
                    <div className="text-xs text-[#57534E]">Odesláno</div>
                    <div className="mono font-medium">{formatQtyWithPackage(d.quantity, product, "ks")}</div>
                  </div>
                  <div>
                    <div className="text-xs text-[#57534E]">Přijato</div>
                    <div className="mono font-medium">
                      {isPending ? "—" : (
                        d.unit_mode && d.unit_mode !== "piece" && d.unit_count
                          ? `${d.unit_count} ${d.unit_mode === "package" ? "bal." : d.unit_mode} (${formatQtyWithPackage(d.received_quantity || 0, product, "ks")})`
                          : formatQtyWithPackage(d.received_quantity || 0, product, "ks")
                      )}
                    </div>
                  </div>
                  {!isPending && (
                    <div className="col-span-2">
                      <div className="text-xs text-[#57534E]">Náklad</div>
                      <div className="mono font-medium">{formatCZK(d.total_cost)}</div>
                    </div>
                  )}
                </div>
                {status === "received_with_issue" && d.discrepancy_note && (
                  <div className="mt-3 text-xs text-[#9E4747] border-t border-[#E5E1D8] pt-2">
                    Rozpor: {d.discrepancy_note}
                  </div>
                )}
                {isPending && canConfirm(d) && (
                  <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-[#E5E1D8]">
                    <button
                      data-testid={`receipt-ok-m-${d.id}`}
                      onClick={() => openOk(d)}
                      className="px-3 py-3 rounded-md bg-[#376A4D] text-white font-medium text-sm active:opacity-80"
                    >
                      Přijato
                    </button>
                    <button
                      data-testid={`receipt-issue-m-${d.id}`}
                      onClick={() => openIssue(d)}
                      className="px-3 py-3 rounded-md border-2 border-[#9E4747] text-[#9E4747] font-medium text-sm active:bg-[#9E47471A]"
                    >
                      S výhradou
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <div className="card-cafe overflow-hidden hidden md:block">
        <table className="w-full text-sm">
          <thead className="bg-[#F9F6F0]">
            <tr className="text-left">
              <th className="p-3 text-xs uppercase tracking-[0.1em] text-[#57534E] font-medium">Čas</th>
              <th className="p-3 text-xs uppercase tracking-[0.1em] text-[#57534E] font-medium">Pobočka</th>
              <th className="p-3 text-xs uppercase tracking-[0.1em] text-[#57534E] font-medium">Výrobek</th>
              <th className="p-3 text-xs uppercase tracking-[0.1em] text-[#57534E] font-medium text-right">Odesláno</th>
              <th className="p-3 text-xs uppercase tracking-[0.1em] text-[#57534E] font-medium text-right">Přijato</th>
              <th className="p-3 text-xs uppercase tracking-[0.1em] text-[#57534E] font-medium text-right">Náklad</th>
              <th className="p-3 text-xs uppercase tracking-[0.1em] text-[#57534E] font-medium">Stav</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td colSpan="8" className="p-10 text-center text-[#57534E]">
                  Žádné záznamy příjmu.
                </td>
              </tr>
            ) : (
              visible.map((d) => {
                const status = d.status || "received";
                const colors = STATUS_COLOR[status];
                const isPending = status === "pending";
                const product = products.find((p) => p.id === d.product_id);
                return (
                  <tr key={d.id} className="border-t border-[#E5E1D8] hover:bg-[#F9F6F0]/50 align-top">
                    <td className="p-3">{formatDateTime(d.created_at)}</td>
                    <td className="p-3 font-medium">{brName(d.branch_id)}</td>
                    <td className="p-3">{prodName(d.product_id)}</td>
                    <td className="p-3 text-right">{formatQtyWithPackage(d.quantity, product, "ks")}</td>
                    <td className="p-3 text-right">
                      {isPending ? "—" : (
                        d.unit_mode && d.unit_mode !== "piece" && d.unit_count
                          ? `${d.unit_count} ${d.unit_mode === "package" ? "bal." : d.unit_mode} (${formatQtyWithPackage(d.received_quantity || 0, product, "ks")})`
                          : formatQtyWithPackage(d.received_quantity || 0, product, "ks")
                      )}
                    </td>
                    <td className="p-3 text-right">{isPending ? "—" : formatCZK(d.total_cost)}</td>
                    <td className="p-3">
                      <span
                        className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium"
                        style={{ background: colors.soft, color: colors.bg }}
                      >
                        {status === "pending" && <Clock className="w-3 h-3" />}
                        {status === "received" && <CheckCircle2 className="w-3 h-3" />}
                        {status === "received_with_issue" && <AlertTriangle className="w-3 h-3" />}
                        {STATUS_LABEL[status]}
                      </span>
                      {status === "received_with_issue" && d.discrepancy_note && (
                        <div className="text-xs text-[#9E4747] mt-1 max-w-xs">
                          Rozpor: {d.discrepancy_note}
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      {isPending && canConfirm(d) && (
                        <div className="flex gap-2 justify-end">
                          <button
                            data-testid={`receipt-ok-${d.id}`}
                            onClick={() => openOk(d)}
                            className="text-xs px-3 py-1.5 rounded-md bg-[#376A4D] text-white hover:opacity-90"
                          >
                            Přijato
                          </button>
                          <button
                            data-testid={`receipt-issue-${d.id}`}
                            onClick={() => openIssue(d)}
                            className="text-xs px-3 py-1.5 rounded-md border border-[#9E4747] text-[#9E4747] hover:bg-[#9E47471A]"
                          >
                            S výhradou
                          </button>
                        </div>
                      )}
                      {!isPending && d.consumed_batches?.length > 0 && (
                        <ul className="space-y-0.5 text-right">
                          {d.consumed_batches.map((cb) => (
                            <li key={cb.batch_id} className="mono text-xs text-[#57534E]">
                              {cb.batch_code}
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-md bg-[#FFFFFF] border-[#E5E1D8]" data-testid="receipt-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }}>
              {dialog?.mode === "ok" ? "Potvrdit příjem" : "Příjem s výhradou"}
            </DialogTitle>
            <DialogDescription>
              {dialog?.mode === "ok"
                ? "Potvrzením vyskladníte výrobky z výrobního skladu a naskladníte je na pobočku."
                : "Zadejte skutečně přijaté množství a popis rozporu. Vyskladní se pouze skutečně přijatá množství."}
            </DialogDescription>
          </DialogHeader>

          {dialog && (
            <div className="space-y-3 pt-1 text-sm">
              <div className="flex justify-between border border-[#E5E1D8] rounded-md p-3 bg-[#F9F6F0]/50">
                <span className="text-[#57534E]">Výrobek</span>
                <span className="font-medium">{prodName(dialog.dispatch.product_id)}</span>
              </div>
              <div className="flex justify-between border border-[#E5E1D8] rounded-md p-3 bg-[#F9F6F0]/50">
                <span className="text-[#57534E]">Pobočka</span>
                <span className="font-medium">{brName(dialog.dispatch.branch_id)}</span>
              </div>
              <div className="flex justify-between border border-[#E5E1D8] rounded-md p-3 bg-[#F9F6F0]/50">
                <span className="text-[#57534E]">Odesláno (ks)</span>
                <span className="mono font-medium">{formatQty(dialog.dispatch.quantity)}</span>
              </div>

              {dialog.mode === "issue" && (
                <>
                  <label className="block">
                    <div className="text-sm font-medium mb-1.5">Skutečně přijato (ks)</div>
                    <input
                      data-testid="receipt-received-qty"
                      type="number"
                      inputMode="numeric"
                      step="1"
                      min="0"
                      max={dialog.dispatch.quantity}
                      className="input-cafe w-full"
                      value={recvQty}
                      onChange={(e) => setRecvQty(e.target.value)}
                    />
                  </label>
                  <label className="block">
                    <div className="text-sm font-medium mb-1.5">Popis rozporu</div>
                    <textarea
                      data-testid="receipt-note"
                      rows={3}
                      className="input-cafe w-full resize-none"
                      placeholder="např. 2 ks rozbité, špatně zabaleno…"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                    />
                  </label>
                </>
              )}
            </div>
          )}

          <DialogFooter>
            <button
              type="button"
              onClick={() => setDialog(null)}
              className="px-4 py-2 rounded-md border border-[#E5E1D8] hover:bg-[#F9F6F0]"
            >
              Zrušit
            </button>
            <button
              data-testid="receipt-confirm"
              onClick={submit}
              disabled={submitting}
              className={
                dialog?.mode === "ok"
                  ? "btn-primary bg-[#376A4D] hover:bg-[#2c553d]"
                  : "btn-primary bg-[#9E4747] hover:bg-[#823737]"
              }
              style={{ color: "white" }}
            >
              {submitting ? "Ukládám…" : dialog?.mode === "ok" ? "Potvrdit Přijato" : "Zapsat výhradu"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="card-cafe p-5">
      <div className="label-eyebrow mb-1">{label}</div>
      <div
        className="text-3xl font-semibold tracking-tight"
        style={{ fontFamily: "Outfit", color: accent || "#1C1917" }}
      >
        {value}
      </div>
    </div>
  );
}
